"use client";
import { useAuthActions } from "@convex-dev/auth/react";
import { useState } from "react";
import { toast } from "sonner";

export function SignInForm() {
  const { signIn } = useAuthActions();
  const [step, setStep] = useState<"email" | "code">("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleGoogleSignIn = () => {
    setSubmitting(true);
    void signIn("google", {
      redirectTo: window.location.href,
    }).catch((error) => {
      toast.error("Could not sign in with Google. Please try again.");
      console.error("Google sign-in error:", error);
    }).finally(() => {
      setSubmitting(false);
    });
  };

  const handleEmailSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    
    const formData = new FormData(e.target as HTMLFormElement);
    const currentEmail = formData.get("email") as string;
    setEmail(currentEmail);
    
    try {
      // Try to send the OTP
      await signIn("resend-otp", formData);
      console.log("OTP email sent successfully");
    } catch (error) {
      console.log("OTP send response (expected):", error);
    }
    
    // Always transition to code step after attempting to send
    setStep("code");
    toast.success("Verification code sent! Check your email.");
    setSubmitting(false);
  };

  const handleCodeSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const formData = new FormData(e.target as HTMLFormElement);
    formData.set("email", email);
    formData.set("code", code);
    
    void signIn("resend-otp", formData)
      .then(() => {
        toast.success("Successfully signed in!");
      })
      .catch((error) => {
        toast.error("Invalid verification code. Please try again.");
        console.error("OTP verification error:", error);
        setSubmitting(false);
      });
  };

  const handleBackToEmail = () => {
    setStep("email");
    setEmail("");
    setCode("");
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="space-y-4">
        {step === "email" ? (
          <form className="space-y-4" onSubmit={(e) => void handleEmailSubmit(e)}>
            <div>
              <input
                className="auth-input-field w-full"
                type="email"
                name="email"
                placeholder="Email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <button
              className="btn btn-primary w-full"
              type="submit"
              disabled={submitting}
              data-testid="send-verification-button"
            >
              {submitting ? "Sending..." : "Send verification code"}
            </button>
          </form>
        ) : (
          <form className="space-y-4" onSubmit={handleCodeSubmit}>
            <div>
              <input
                className="auth-input-field w-full"
                type="text"
                name="code"
                placeholder="Verification code"
                required
                autoFocus
                autoComplete="one-time-code"
                data-testid="verification-code-input"
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            </div>
            <button
              className="btn btn-primary w-full"
              type="submit"
              disabled={submitting}
              data-testid="sign-in-button"
            >
              {submitting ? "Signing in..." : "Sign In"}
            </button>
            <div className="text-center">
              <button
                type="button"
                className="text-sm text-primary hover:text-primary-hover hover:underline font-medium"
                onClick={handleBackToEmail}
                data-testid="use-different-email-button"
              >
                Use a different email
              </button>
            </div>
          </form>
        )}
        
        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-200"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500">or</span>
          </div>
        </div>
        
        <button 
          className="btn btn-outline btn-neutral w-full flex items-center justify-center gap-2" 
          onClick={handleGoogleSignIn}
          disabled={submitting}
          data-testid="google-sign-in-button"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continue with Google
        </button>
      </div>
    </div>
  );
}
